import * as React from 'react';
import { useState, useEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  FaChevronLeft,
  FaChevronRight,
  FaFlag,
  FaGlobe,
  FaCity,
  FaMapMarkerAlt,
  FaStar,
  FaPlus,
  FaTimes,
  FaTrashAlt,
} from 'react-icons/fa';
import { CHART_COLORS } from '../../../utils/chartColors';
import { colors } from '../../employees/styles/personalakteStyles';
import EventService from '../services/EventService';
import { EventsResponse, UnifiedEvent, CustomEvent } from '../types';
import { unifyEvents, eventsOnDate } from '../services/unifyEvents';
import TenantService from '../../settings/company/services/TenantService';
import { toast } from 'react-toastify';

const HOLIDAY_COLOR = '#16a34a';
const EVENT_COLOR = '#3b82f6';
const MONTHS_DE = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];
const WEEKDAYS_SHORT = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

const TYPE_LABELS: Record<string, string> = {
  public_holiday: 'Feiertag',
  world_day: 'Welttag',
  city_event: 'City Event',
  local_event: 'Lokales Event',
  custom_event: 'Eigenes Event',
};

const TYPE_ICONS: Record<string, React.ReactNode> = {
  public_holiday: <FaFlag size={12} />,
  world_day: <FaGlobe size={12} />,
  city_event: <FaCity size={12} />,
  local_event: <FaMapMarkerAlt size={12} />,
  custom_event: <FaStar size={12} />,
};

const CUSTOM_CATEGORIES = [
  { value: 'festival', label: 'Festival' },
  { value: 'concert', label: 'Konzert' },
  { value: 'sport', label: 'Sport' },
  { value: 'messe', label: 'Messe' },
  { value: 'market', label: 'Markt' },
  { value: 'community', label: 'Community' },
  { value: 'cultural', label: 'Kultur' },
  { value: 'other', label: 'Sonstiges' },
];

function formatDateDE(dateStr: string): string {
  const d = new Date(dateStr + 'T12:00:00');
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function toISO(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

function getMonthGrid(year: number, month: number): (string | null)[][] {
  const firstDay = new Date(year, month, 1);
  let startWeekday = firstDay.getDay() - 1;
  if (startWeekday < 0) startWeekday = 6;
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const weeks: (string | null)[][] = [];
  let currentWeek: (string | null)[] = new Array(startWeekday).fill(null);

  for (let d = 1; d <= daysInMonth; d++) {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    currentWeek.push(dateStr);
    if (currentWeek.length === 7) {
      weeks.push(currentWeek);
      currentWeek = [];
    }
  }
  if (currentWeek.length > 0) {
    while (currentWeek.length < 7) currentWeek.push(null);
    weeks.push(currentWeek);
  }
  return weeks;
}

interface Location {
  id: number;
  name: string;
  is_headquarters?: boolean;
}

export default function EventsCalendar(): React.ReactElement {
  const [year, setYear] = useState(new Date().getFullYear());
  const [events, setEvents] = useState<UnifiedEvent[]>([]);
  const [rawData, setRawData] = useState<EventsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [locations, setLocations] = useState<Location[]>([]);
  const [selectedLocationId, setSelectedLocationId] = useState<number | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    TenantService.getLocations()
      .then((locs: Location[]) => {
        setLocations(locs);
        const hq = locs.find(l => l.is_headquarters);
        setSelectedLocationId(hq?.id || locs[0]?.id || null);
      })
      .catch(() => { setError('Standorte konnten nicht geladen werden.'); });
  }, []);

  useEffect(() => {
    if (!selectedLocationId) return;
    setLoading(true);
    setError(null);
    const start = `${year}-01-01`;
    EventService.getEvents(selectedLocationId, start, 365)
      .then(data => {
        setRawData(data);
        setEvents(unifyEvents(data));
      })
      .catch(() => { setEvents([]); setRawData(null); setError('Events konnten nicht geladen werden.'); })
      .finally(() => setLoading(false));
  }, [selectedLocationId, year]);

  const eventsByDate = useMemo(() => {
    const map = new Map<string, UnifiedEvent[]>();
    for (const ev of events) {
      const addToDate = (d: string) => {
        const list = map.get(d) || [];
        list.push(ev);
        map.set(d, list);
      };
      addToDate(ev.date);
      if (ev.endDate && ev.endDate !== ev.date) {
        const start = new Date(ev.date + 'T12:00:00');
        const end = new Date(ev.endDate + 'T12:00:00');
        const cursor = new Date(start);
        cursor.setDate(cursor.getDate() + 1);
        while (cursor <= end) {
          addToDate(toISO(cursor));
          cursor.setDate(cursor.getDate() + 1);
        }
      }
    }
    return map;
  }, [events]);

  const selectedEvents = useMemo(() => {
    if (!selectedDate) return [];
    return eventsOnDate(events, selectedDate);
  }, [selectedDate, events]);

  const reload = useCallback(() => {
    if (!selectedLocationId) return;
    const start = `${year}-01-01`;
    EventService.getEvents(selectedLocationId, start, 365)
      .then(data => {
        setRawData(data);
        setEvents(unifyEvents(data));
      })
      .catch(() => { setError('Events konnten nicht neu geladen werden.'); });
  }, [selectedLocationId, year]);

  const handleDeleteCustom = async (id: number) => {
    try {
      await EventService.deleteCustomEvent(id);
      toast.success('Event gelöscht');
      reload();
    } catch {
      toast.error('Fehler beim Löschen');
    }
  };

  const todayStr = toISO(new Date());

  return (
    <div style={{ padding: 24, maxWidth: 1400, margin: '0 auto', minHeight: '100vh' }}>
      {/* Header */}
      <div className="glass-header" style={{ marginBottom: 24, padding: '16px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: CHART_COLORS.navy }}>
            Events & Feiertage {year}
          </h1>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <button
              onClick={() => setYear(y => y - 1)}
              className="glass-btn glass-btn-secondary"
              style={{ padding: '6px 10px', fontSize: 12 }}
            >
              <FaChevronLeft size={10} />
            </button>
            <button
              onClick={() => setYear(new Date().getFullYear())}
              className="glass-btn glass-btn-secondary"
              style={{ padding: '6px 12px', fontSize: 12 }}
            >
              Heute
            </button>
            <button
              onClick={() => setYear(y => y + 1)}
              className="glass-btn glass-btn-secondary"
              style={{ padding: '6px 10px', fontSize: 12 }}
            >
              <FaChevronRight size={10} />
            </button>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {locations.length > 1 && (
            <select
              value={selectedLocationId || ''}
              onChange={e => setSelectedLocationId(Number(e.target.value))}
              style={{
                padding: '6px 12px',
                borderRadius: 8,
                border: '1px solid #e5e7eb',
                fontSize: 13,
                color: CHART_COLORS.navy,
                background: 'white',
              }}
            >
              {locations.map(l => (
                <option key={l.id} value={l.id}>{l.name}</option>
              ))}
            </select>
          )}
          <button
            onClick={() => setShowCreateModal(true)}
            className="glass-btn glass-btn-primary"
            style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}
          >
            <FaPlus size={11} /> Event erstellen
          </button>
        </div>
      </div>

      {/* Legend */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 16, paddingLeft: 4 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <div style={{ width: 10, height: 10, borderRadius: '50%', background: HOLIDAY_COLOR }} />
          <span style={{ fontSize: 12, color: '#666' }}>Feiertag</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <div style={{ width: 10, height: 10, borderRadius: '50%', background: EVENT_COLOR }} />
          <span style={{ fontSize: 12, color: '#666' }}>Event</span>
        </div>
      </div>

      {error && (
        <div style={{ padding: 12, marginBottom: 12, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, color: '#dc2626', fontSize: 13 }}>
          {error}
        </div>
      )}

      {loading ? (
        <div style={{ padding: 60, textAlign: 'center', color: '#888' }}>Laden...</div>
      ) : (
        <div style={{ display: 'flex', gap: 24 }}>
          {/* Calendar Grid */}
          <div style={{ flex: 1, display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
            {Array.from({ length: 12 }, (_, monthIdx) => {
              const weeks = getMonthGrid(year, monthIdx);
              return (
                <div key={monthIdx} className="glass-card" style={{ padding: 12 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: CHART_COLORS.navy, marginBottom: 8, textAlign: 'center' }}>
                    {MONTHS_DE[monthIdx]}
                  </div>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        {WEEKDAYS_SHORT.map(wd => (
                          <th key={wd} style={{ fontSize: 10, color: '#94a3b8', fontWeight: 500, padding: '2px 0', textAlign: 'center' }}>
                            {wd}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {weeks.map((week, wi) => (
                        <tr key={wi}>
                          {week.map((dateStr, di) => {
                            if (!dateStr) return <td key={di} style={{ padding: 2 }} />;
                            const dayNum = parseInt(dateStr.split('-')[2], 10);
                            const dayEvents = eventsByDate.get(dateStr) || [];
                            const hasHoliday = dayEvents.some(e => e.isHoliday);
                            const hasEvent = dayEvents.some(e => !e.isHoliday);
                            const isToday = dateStr === todayStr;
                            const isSelected = dateStr === selectedDate;

                            return (
                              <td
                                key={di}
                                onClick={() => setSelectedDate(dateStr === selectedDate ? null : dateStr)}
                                style={{
                                  padding: 2,
                                  textAlign: 'center',
                                  cursor: 'pointer',
                                  position: 'relative',
                                }}
                              >
                                <div
                                  style={{
                                    width: 28,
                                    height: 28,
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: 11,
                                    fontWeight: isToday || isSelected ? 700 : 400,
                                    color: isSelected ? '#fff' : isToday ? CHART_COLORS.navy : '#334155',
                                    background: isSelected ? CHART_COLORS.navy : isToday ? '#e2e8f0' : 'transparent',
                                    margin: '0 auto',
                                    transition: 'background 0.15s',
                                  }}
                                >
                                  {dayNum}
                                </div>
                                {(hasHoliday || hasEvent) && (
                                  <div style={{ display: 'flex', justifyContent: 'center', gap: 2, marginTop: 1 }}>
                                    {hasHoliday && <div style={{ width: 4, height: 4, borderRadius: '50%', background: HOLIDAY_COLOR }} />}
                                    {hasEvent && <div style={{ width: 4, height: 4, borderRadius: '50%', background: EVENT_COLOR }} />}
                                  </div>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              );
            })}
          </div>

          {/* Detail Panel */}
          <div style={{ width: 340, flexShrink: 0 }}>
            <div className="glass-card" style={{ padding: 16, position: 'sticky', top: 24 }}>
              {selectedDate ? (
                <>
                  <div style={{ fontSize: 16, fontWeight: 700, color: CHART_COLORS.navy, marginBottom: 12 }}>
                    {formatDateDE(selectedDate)}
                  </div>
                  {selectedEvents.length === 0 ? (
                    <div style={{ padding: 16, textAlign: 'center', color: '#888', fontSize: 13 }}>
                      Keine Events an diesem Tag
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {selectedEvents.map((ev, i) => {
                        const color = ev.isHoliday ? HOLIDAY_COLOR : EVENT_COLOR;
                        const isCustom = ev.type === 'custom_event';
                        return (
                          <div
                            key={`${ev.type}-${ev.name}-${i}`}
                            style={{
                              padding: '10px 12px',
                              borderRadius: 8,
                              border: '1px solid #e5e7eb',
                              background: ev.isHoliday ? '#f0fdf4' : '#eff6ff',
                            }}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                              <span style={{ color }}>{TYPE_ICONS[ev.type]}</span>
                              <span style={{ fontSize: 14, fontWeight: 600, color: CHART_COLORS.navy, flex: 1 }}>
                                {ev.name}
                              </span>
                              {isCustom && (
                                <button
                                  onClick={() => handleDeleteCustom((ev.raw as CustomEvent).id)}
                                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', padding: 2 }}
                                  title="Löschen"
                                >
                                  <FaTrashAlt size={11} />
                                </button>
                              )}
                            </div>
                            <div style={{ fontSize: 12, color: '#64748b', marginBottom: 2 }}>
                              {TYPE_LABELS[ev.type]}
                              {ev.endDate && ev.endDate !== ev.date && (
                                <span> &middot; bis {formatDateDE(ev.endDate)}</span>
                              )}
                            </div>
                            {ev.detail && (
                              <div style={{ fontSize: 12, color: '#64748b' }}>{ev.detail}</div>
                            )}
                            {ev.location && (
                              <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>
                                <FaMapMarkerAlt size={9} style={{ marginRight: 4 }} />
                                {ev.location}
                              </div>
                            )}
                            {ev.visitors && ev.visitors > 0 && (
                              <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>
                                ~{ev.visitors.toLocaleString('de-DE')} Besucher erwartet
                              </div>
                            )}
                            {ev.keywords && ev.keywords.length > 0 && (
                              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 6 }}>
                                {ev.keywords.slice(0, 5).map((kw, ki) => (
                                  <span
                                    key={ki}
                                    style={{
                                      fontSize: 10,
                                      padding: '2px 6px',
                                      borderRadius: 8,
                                      background: ev.isHoliday ? '#dcfce7' : '#dbeafe',
                                      color: ev.isHoliday ? '#166534' : '#1e40af',
                                      fontWeight: 500,
                                    }}
                                  >
                                    {kw}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </>
              ) : (
                <div style={{ padding: 24, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
                  Klicke auf einen Tag um Details zu sehen
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Create Custom Event Modal */}
      {showCreateModal && (
        <CreateEventModal
          locationId={selectedLocationId}
          locations={locations}
          onClose={() => setShowCreateModal(false)}
          onCreated={() => { setShowCreateModal(false); reload(); }}
          defaultDate={selectedDate}
        />
      )}
    </div>
  );
}

interface CreateEventModalProps {
  locationId: number | null;
  locations: Location[];
  onClose: () => void;
  onCreated: () => void;
  defaultDate: string | null;
}

function CreateEventModal({ locationId, locations, onClose, onCreated, defaultDate }: CreateEventModalProps): React.ReactElement {
  const [name, setName] = useState('');
  const [startDate, setStartDate] = useState(defaultDate || toISO(new Date()));
  const [endDate, setEndDate] = useState('');
  const [category, setCategory] = useState('other');
  const [description, setDescription] = useState('');
  const [expectedVisitors, setExpectedVisitors] = useState('');
  const [keywordsStr, setKeywordsStr] = useState('');
  const [isRecurring, setIsRecurring] = useState(false);
  const [eventLocationId, setEventLocationId] = useState<string>(locationId ? String(locationId) : '');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!name.trim() || !startDate) {
      toast.error('Name und Startdatum sind Pflichtfelder');
      return;
    }
    setSaving(true);
    try {
      const keywords = keywordsStr.split(',').map(k => k.trim()).filter(Boolean);
      await EventService.createCustomEvent({
        name: name.trim(),
        start_date: startDate,
        end_date: endDate || undefined,
        category: category as CustomEvent['category'],
        description: description.trim() || undefined,
        expected_visitors: expectedVisitors ? parseInt(expectedVisitors, 10) : undefined,
        keywords: keywords.length > 0 ? keywords : undefined,
        is_recurring_yearly: isRecurring,
        location_id: eventLocationId ? parseInt(eventLocationId, 10) : null,
      });
      toast.success('Event erstellt');
      onCreated();
    } catch {
      toast.error('Fehler beim Erstellen');
    } finally {
      setSaving(false);
    }
  };

  const fieldStyle: React.CSSProperties = {
    width: '100%',
    padding: '8px 12px',
    borderRadius: 8,
    border: '1px solid #e5e7eb',
    fontSize: 13,
    color: CHART_COLORS.navy,
    boxSizing: 'border-box',
  };

  const labelStyle: React.CSSProperties = {
    fontSize: 12,
    fontWeight: 600,
    color: '#64748b',
    marginBottom: 4,
    display: 'block',
  };

  return createPortal(
    <>
      <div
        style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.3)', zIndex: 1000 }}
        onClick={onClose}
      />
      <div
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          background: 'white',
          borderRadius: 12,
          padding: 24,
          zIndex: 1001,
          width: 440,
          maxHeight: '85vh',
          overflowY: 'auto',
          boxShadow: '0 8px 30px rgba(0,0,0,0.2)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: CHART_COLORS.navy }}>
            Eigenes Event erstellen
          </h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8' }}>
            <FaTimes size={16} />
          </button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <label style={labelStyle}>Name *</label>
            <input value={name} onChange={e => setName(e.target.value)} style={fieldStyle} placeholder="z.B. Straßenfest Bergmannstraße" />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={labelStyle}>Startdatum *</label>
              <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} style={fieldStyle} />
            </div>
            <div>
              <label style={labelStyle}>Enddatum</label>
              <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} style={fieldStyle} />
            </div>
          </div>

          <div>
            <label style={labelStyle}>Kategorie</label>
            <select value={category} onChange={e => setCategory(e.target.value)} style={fieldStyle}>
              {CUSTOM_CATEGORIES.map(c => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label style={labelStyle}>Beschreibung</label>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              style={{ ...fieldStyle, minHeight: 60, resize: 'vertical' }}
              placeholder="Optionale Beschreibung..."
            />
          </div>

          <div>
            <label style={labelStyle}>Erwartete Besucher</label>
            <input type="number" value={expectedVisitors} onChange={e => setExpectedVisitors(e.target.value)} style={fieldStyle} placeholder="z.B. 5000" />
          </div>

          <div>
            <label style={labelStyle}>Keywords (kommagetrennt)</label>
            <input value={keywordsStr} onChange={e => setKeywordsStr(e.target.value)} style={fieldStyle} placeholder="z.B. Outdoor-Bar, Live-Musik, Straßenfest-Menü" />
          </div>

          {locations.length > 1 && (
            <div>
              <label style={labelStyle}>Standort (leer = alle)</label>
              <select value={eventLocationId} onChange={e => setEventLocationId(e.target.value)} style={fieldStyle}>
                <option value="">Alle Standorte</option>
                {locations.map(l => (
                  <option key={l.id} value={l.id}>{l.name}</option>
                ))}
              </select>
            </div>
          )}

          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: CHART_COLORS.navy, cursor: 'pointer' }}>
            <input type="checkbox" checked={isRecurring} onChange={e => setIsRecurring(e.target.checked)} />
            Jährlich wiederkehrend
          </label>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
          <button onClick={onClose} className="glass-btn glass-btn-secondary" style={{ fontSize: 13 }}>
            Abbrechen
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="glass-btn glass-btn-primary"
            style={{ fontSize: 13 }}
          >
            {saving ? 'Speichern...' : 'Event erstellen'}
          </button>
        </div>
      </div>
    </>,
    document.body
  );
}
